// ---------------------------------------------------------------------------
// imageLoading — reveal images once they have decoded.
//
// The legacy stylesheet hides images until their container is "loaded":
//
//   .loading img { opacity: 0 }
//   .loading .loadingSpinner { opacity: 1 }
//
// (see legacy/imageLoading.scss). The original theme removed the `loading`
// class from JS once the image had downloaded; without that, every image on the
// site stays invisible behind a spinner. This restores that behaviour.
//
// Markup contract (macros/image.html): a container carrying `.loading` wraps an
// <img> and a sibling `.loadingSpinner`.
// ---------------------------------------------------------------------------

const settle = (container: HTMLElement): void => {
	const images = Array.from(container.querySelectorAll('img'));
	if (!images.length) {
		container.classList.remove('loading');
		return;
	}

	let outstanding = images.length;
	const done = (): void => {
		outstanding -= 1;
		if (outstanding <= 0) container.classList.remove('loading');
	};

	for (const image of images) {
		// `complete` covers cached images, which never fire `load`.
		if (image.complete) {
			done();
			continue;
		}
		image.addEventListener('load', done, { once: true });
		// Reveal on error too: a spinner that never stops is worse than a
		// broken-image icon, and it would hide the rest of the container.
		image.addEventListener('error', done, { once: true });
	}
};

export const initImageLoading = (): void => {
	for (const container of document.querySelectorAll<HTMLElement>('.loading')) settle(container);
};
